// Pure logic for the Leave > Calendar todo list: ordering, per-day view-model,
// tag/field validation and the floating card's placement. No React in here so
// it can be unit tested directly.

export type TodoCategory = "important" | "work" | "reminder" | "travel" | "personal";

export interface CalendarTodo {
  id: string;
  /** ISO date or datetime from the API; always read through `todoDateKey`. */
  date: string;
  title: string;
  description: string;
  /** Stored tone (see calendarNoteCategories.ts); `categoryOf` maps it to a category. */
  tone: string;
  tags: string[];
  time: string | null;
  done: boolean;
  doneAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TodoDraft {
  title: string;
  description: string;
  category: TodoCategory;
  tags: string[];
  time: string | null;
  date: string;
}

export const TODO_LIMITS = { title: 120, description: 2000, tags: 8, tagLength: 24 } as const;

/** Priority order: Important, Work, Reminder, Travel, Personal. */
export const CATEGORY_ORDER: TodoCategory[] = ["important", "work", "reminder", "travel", "personal"];

// The five stored tones double as the five categories (no data migration needed).
const TONE_BY_CATEGORY: Record<TodoCategory, string> = {
  important: "danger", work: "info", reminder: "warning", travel: "cyan", personal: "accent",
};

export const CATEGORY_LABEL: Record<TodoCategory, string> = {
  important: "Important", work: "Work", reminder: "Reminder", travel: "Travel", personal: "Personal",
};

export function toneOf(category: TodoCategory): string { return TONE_BY_CATEGORY[category]; }

export function categoryOf(tone: string): TodoCategory {
  return CATEGORY_ORDER.find(c => TONE_BY_CATEGORY[c] === tone) ?? "work";
}

export function todoDateKey(t: Pick<CalendarTodo, "date">): string { return t.date.split("T")[0]!; }

export function isoDate(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export function todayKey(now = new Date()): string {
  return isoDate(now.getFullYear(), now.getMonth(), now.getDate());
}

/** Open first; open: timed (ascending) then untimed by category priority, then creation; done: latest completed first. */
export function compareTodos(a: CalendarTodo, b: CalendarTodo): number {
  if (a.done !== b.done) return a.done ? 1 : -1;
  if (a.done) return (b.doneAt ?? "").localeCompare(a.doneAt ?? "");
  if (a.time && b.time) return a.time.localeCompare(b.time) || a.createdAt.localeCompare(b.createdAt);
  if (a.time) return -1;
  if (b.time) return 1;
  const pa = CATEGORY_ORDER.indexOf(categoryOf(a.tone));
  const pb = CATEGORY_ORDER.indexOf(categoryOf(b.tone));
  return pa - pb || a.createdAt.localeCompare(b.createdAt);
}

export const MAX_CIRCLES = 5;

export interface DayTodoView {
  sorted: CalendarTodo[];
  circles: CalendarTodo[];
  overflow: number;
  total: number;
  doneCount: number;
  openCount: number;
  /** Open todos on a date before today. */
  overdue: number;
}

export function buildDayView(todos: CalendarTodo[], dateKey: string, today: string, maxCircles = MAX_CIRCLES): DayTodoView {
  const sorted = [...todos].sort(compareTodos);
  const doneCount = sorted.filter(t => t.done).length;
  const openCount = sorted.length - doneCount;
  return {
    sorted,
    circles: sorted.slice(0, maxCircles),
    overflow: Math.max(0, sorted.length - maxCircles),
    total: sorted.length,
    doneCount,
    openCount,
    overdue: dateKey < today ? openCount : 0,
  };
}

export function groupByDate(todos: CalendarTodo[]): Map<string, CalendarTodo[]> {
  const m = new Map<string, CalendarTodo[]>();
  for (const t of todos) {
    const k = todoDateKey(t);
    const list = m.get(k);
    if (list) list.push(t); else m.set(k, [t]);
  }
  return m;
}

export interface MonthSummary { open: number; done: number; overdue: number }

export function summarize(todos: CalendarTodo[], today: string): MonthSummary {
  let open = 0, done = 0, overdue = 0;
  for (const t of todos) {
    if (t.done) done++;
    else { open++; if (todoDateKey(t) < today) overdue++; }
  }
  return { open, done, overdue };
}

type GraphemeSegmenter = new (l?: string, o?: object) => { segment(s: string): Iterable<{ segment: string }> };
 
/**
 * Fills the fields an API row can lack (older rows only have `note`, and a server
 * behind the web build may not send the newer columns), so views never see undefined.
 */
export function normalizeTodo(raw: CalendarTodo & { note?: string | null }): CalendarTodo {
  return {
    ...raw,
    title: raw.title || raw.note || "",
    description: raw.description ?? "",
    tone: raw.tone ?? "info",
    tags: raw.tags ?? [],
    time: raw.time ?? null,
    done: !!raw.done,
    doneAt: raw.doneAt ?? null,
  };
}



export function firstLetter(title: string | null | undefined): string {
  const t = (title ?? "").trim();
  if (!t) return "?";
  const Seg = (Intl as unknown as { Segmenter?: GraphemeSegmenter }).Segmenter;
  const first = Seg ? [...new Seg(undefined, { granularity: "grapheme" }).segment(t)][0]?.segment ?? [...t][0]! : [...t][0]!;
  return first.toUpperCase();
}

/** Strips a leading #, trims, drops empty/over-long, de-dupes case-insensitively, caps at 8. */
export function normalizeTags(raw: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of raw) {
    const t = r.trim().replace(/^#+/, "").trim();
    if (!t || t.length > TODO_LIMITS.tagLength) continue;
    const k = t.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t);
    if (out.length === TODO_LIMITS.tags) break;
  }
  return out;
}

/** Splits typed/pasted tag text on commas and newlines. */
export function splitTagInput(text: string): string[] {
  return text.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
}

/**
 * `today` + `originalDate` enforce "no todo before today": a draft may only be
 * dated today or later, except that an existing todo may keep its own past date.
 */
export function validateDraft(d: TodoDraft, today?: string, originalDate?: string): { title?: string; description?: string; date?: string } {
  const errors: { title?: string; description?: string; date?: string } = {};
  if (today && d.date < today && d.date !== originalDate) errors.date = "Todos can't be set before today";
  const title = d.title.trim();
  if (!title) errors.title = "Title is required";
  else if (title.length > TODO_LIMITS.title) errors.title = `Title can be at most ${TODO_LIMITS.title} characters`;
  if (d.description.length > TODO_LIMITS.description) errors.description = `Description can be at most ${TODO_LIMITS.description} characters`;
  return errors;
}

export interface PlaceCardInput {
  col: number; row: number; rows: number;
  cellW: number; cellH: number; headH: number;
  cardW: number; cardH: number;
}
export interface PlaceCardResult { left: number; top: number; side: "left" | "right"; arrowY: number }

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export function placeCard({ col, row, rows, cellW, cellH, headH, cardW, cardH }: PlaceCardInput): PlaceCardResult {
  const calH = headH + rows * cellH;
  const top = clamp(headH + row * cellH - 24, 64, Math.max(64, calH - cardH - 8));
  const toRight = col <= 3; // Sun..Wed open to the right, Thu..Sat to the left
  const left = toRight ? (col + 1) * cellW + 10 : col * cellW - cardW - 10;
  const arrowY = clamp(headH + row * cellH + cellH / 2 - top - 6, 22, cardH - 36);
  return { left, top, side: toRight ? "left" : "right", arrowY };
}
